import React from 'react';
import { DatePicker } from 'antd';
import { Dayjs } from 'dayjs';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';
import { useAntdPickerLocale } from '../hooks/useAntdPickerLocale';

/**
 * A "from / to" window as two separate date fields.
 *
 * Deliberately NOT antd's `RangePicker`, which opens two month panels side by
 * side: that popup wants about 560px, which is wider than any phone, so on a
 * phone it simply came out cut off. One field, one panel, ~280px -- fits, and
 * "Desde" and "Hasta" as their own labelled inputs are easier to reason about
 * than two halves of one control anyway.
 *
 * Same shape on both sizes. A phone-only variant would have meant two layouts
 * of the same control to keep in step, and this one has no reason to differ.
 *
 * The two fields bound each other -- the start can never be picked past the
 * end, nor the end before the start -- so the pair can only ever produce a
 * valid window, rather than letting somebody build an impossible one and
 * finding out from an error.
 */
export interface IDateRangeFieldsProps {
  value: [Dayjs, Dayjs];
  onChange: (next: [Dayjs, Dayjs]) => void;
  /** Outer bounds. Use them to keep the widest possible window inside whatever the server accepts. */
  earliest?: Dayjs;
  latest?: Dayjs;
  fromPlaceholder: string;
  toPlaceholder: string;
  style?: React.CSSProperties;
}

const DateRangeFields: React.FC<IDateRangeFieldsProps> = ({
  value,
  onChange,
  earliest,
  latest,
  fromPlaceholder,
  toPlaceholder,
  style,
}) => {
  const { isMobile } = useAppBreakpoint();
  const locale = useAntdPickerLocale();

  const [from, to] = value;

  const outOfBounds = (date: Dayjs) =>
    (!!earliest && date.isBefore(earliest, 'day')) || (!!latest && date.isAfter(latest, 'day'));

  return (
    <div style={{ display: 'flex', gap: 8, minWidth: 0, ...style }}>
      <DatePicker
        locale={locale}
        allowClear={false}
        // A phone's own keyboard over a calendar is worse than no keyboard:
        // the field is a display, the panel is the control.
        inputReadOnly={isMobile}
        popupClassName="heavyDutyMachinery-picker-popup"
        style={{ flex: '1 1 0', minWidth: 0 }}
        format="DD/MM/YYYY"
        placeholder={fromPlaceholder}
        value={from}
        disabledDate={(date) => outOfBounds(date) || date.isAfter(to, 'day')}
        onChange={(date) => date && onChange([date.startOf('day'), to])}
      />
      <DatePicker
        locale={locale}
        allowClear={false}
        inputReadOnly={isMobile}
        popupClassName="heavyDutyMachinery-picker-popup"
        style={{ flex: '1 1 0', minWidth: 0 }}
        format="DD/MM/YYYY"
        placeholder={toPlaceholder}
        value={to}
        disabledDate={(date) => outOfBounds(date) || date.isBefore(from, 'day')}
        onChange={(date) => date && onChange([from, date.endOf('day')])}
      />
    </div>
  );
};

export default DateRangeFields;
